import React, { useState, useEffect, useMemo } from 'react';
import { SalesRep } from '../types';
import { crmDataService } from '../services/crmDataService';
import { TrendingUp, Loader2, Calendar, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';

interface SalesAnalysisProps {
  salesReps: SalesRep[];
  currentUser: any;
  globalRefreshKey?: number; // App.tsx에서 전달받는 글로벌 새로고침 키
}

const SalesAnalysis: React.FC<SalesAnalysisProps> = ({ salesReps, globalRefreshKey = 0 }) => {
  const [targets, setTargets] = useState<any[]>([]); // DB: sales_targets 데이터
  const [forecastData, setForecastData] = useState<any[]>([]);
  const [targetsLoading, setTargetsLoading] = useState(true);
  const [forecastLoading, setForecastLoading] = useState(true);
  
  // 🚨 [수정] 기존 selectedMonth를 대신하여 세밀한 일자 필터링을 위한 시작/종료일 상태 추가
  const currentYear = new Date().getFullYear();
  const [startDate, setStartDate] = useState<string>(`${currentYear}-01-01`);
  const [endDate, setEndDate] = useState<string>(`${currentYear}-12-31`);

  // =================================================================
  // 📡 [SECTION 1] 데이터 통신 (Data Fetching)
  // =================================================================

  useEffect(() => {
    const fetchTargets = async () => {
      try {
        const json = await crmDataService.getSalesTargets();
        if (json.status === 'success') {
          setTargets(json.data);
        }
      } catch (err) {
        console.error("목표 데이터 로드 실패", err);
      } finally {
        setTargetsLoading(false);
      }
    };
    fetchTargets();
  }, [globalRefreshKey]); // globalRefreshKey가 변경될 때마다 목표 데이터 새로고침

  useEffect(() => {
    let mounted = true;
    crmDataService.getSalesForecastList()
      .then((data) => { if (mounted) setForecastData(data || []); })
      .catch(() => { if (mounted) setForecastData([]); })
      .finally(() => { if (mounted) setForecastLoading(false); });
    return () => { mounted = false; };
  }, [globalRefreshKey]); // globalRefreshKey가 변경될 때마다 실적 데이터 새로고침

  // =================================================================
  // ⚙️ [SECTION 2] 데이터 보정 유틸리티
  // =================================================================

  const safeParseNumber = (val: any) => {
    if (val === undefined || val === null || val === "") return 0;
    if (typeof val === 'number') return val;
    return Number(String(val).replace(/,/g, '')) || 0;
  };

  // =================================================================
  // 🧠 [SECTION 3] 핵심 집계 로직 (Data Aggregation)
  // =================================================================

  const performanceData = useMemo(() => {
    if (targetsLoading || forecastLoading) return [];

    const summary = new Map<string, {
      name: string;
      targetProduct: number;
      targetItem: number;
      actualProduct: number;
      actualItem: number;
    }>();

    // 🚨 [수정] 목표 합산을 위해 선택된 날짜의 시작월과 종료월 인덱스 계산 (0~11)
    const startM = new Date(startDate).getMonth();
    const endM = new Date(endDate).getMonth();

    // 1. 목표 데이터를 먼저 돌면서 '기준 명단'을 만듭니다.
    targets.forEach((t: any) => {
      const name = (t.manager || "").trim();
      if (!name) return;

      if (!summary.has(name)) {
        summary.set(name, {
          name: name,
          targetProduct: 0, targetItem: 0,
          actualProduct: 0, actualItem: 0
        });
      }

      const record = summary.get(name)!;
      let amount = 0;

      // 🚨 [수정] 사용자가 설정한 기간 내에 포함된 모든 월의 목표를 합산 (예: 2월~5월 선택 시 해당 월들 합산)
      for (let i = startM; i <= endM; i++) {
        amount += safeParseNumber(t.months[i]);
      }

      if (t.type === '제품') record.targetProduct += amount;
      if (t.type === '상품') record.targetItem += amount;
    });

    // 2. 실적 데이터를 돌면서 명단에 매칭시키거나, 명단에 없으면 새로 추가합니다.
    forecastData.forEach(item => {
      const repName = (item.담당자 || "").trim();
      if (!repName || repName === '미정') return;

      // 인정 기준 확인
      const status = (item.구분 || '').trim();
      const isActual = status.includes('실적') || status.includes('완료') || status.includes('매출') || status.includes('계약완료');
      if (!isActual) return;

      // 날짜 필터링
      // 🚨 [수정] 매출예정일이 시작일(startDate)과 종료일(endDate) 사이에 정확히 있는지 확인
      const itemDate = item.매출예정일 || '';
      if (itemDate < startDate || itemDate > endDate) return;

      // 🚨 [핵심 수정] 목표 리스트에 없어도 실적 데이터가 있으면 리스트에 추가하여 보여줍니다.
      if (!summary.has(repName)) {
        summary.set(repName, {
          name: repName,
          targetProduct: 0, targetItem: 0,
          actualProduct: 0, actualItem: 0
        });
      }

      const record = summary.get(repName)!;
      record.actualProduct += safeParseNumber(item.제품금액);
      record.actualItem += safeParseNumber(item.상품금액);
    });

    return Array.from(summary.values());
    // 🚨 [수정] 날짜 범위 변경 시 재계산되도록 의존성 배열에 startDate, endDate 추가
  }, [forecastData, targets, targetsLoading, forecastLoading, startDate, endDate]);

  const totalRow = useMemo(() => {
    return performanceData.reduce((acc, cur) => ({
      targetProduct: acc.targetProduct + cur.targetProduct,
      targetItem: acc.targetItem + cur.targetItem,
      actualProduct: acc.actualProduct + cur.actualProduct,
      actualItem: acc.actualItem + cur.actualItem,
    }), { targetProduct: 0, targetItem: 0, actualProduct: 0, actualItem: 0 });
  }, [performanceData]);

  if (targetsLoading || forecastLoading) return <div className="p-10 text-center"><Loader2 className="animate-spin inline text-blue-600" /> 데이터 분석 중...</div>;

  // =================================================================
  // 📂 [SECTION 4] 엑셀 다운로드
  // =================================================================
  const handleExportAnalysisExcel = () => {
    const rows = performanceData.map((row) => {
      const targetTotal = row.targetProduct + row.targetItem;
      const actualTotal = row.actualProduct + row.actualItem;
      const rate = targetTotal > 0 ? actualTotal / targetTotal : 0; // 0~1
      return {
        name: row.name,
        targetProduct: row.targetProduct,
        targetItem: row.targetItem,
        targetTotal,
        actualProduct: row.actualProduct,
        actualItem: row.actualItem,
        actualTotal,
        rate
      };
    });

    // 체크/확인 팝업 추가
    if (!rows.length) {
      alert('내보낼 데이터가 없습니다.');
      return;
    }

    const ok = window.confirm(
      `총 ${rows.length}건을 엑셀로 저장할까요?`
    );
    if (!ok) return;

    const totalTarget = totalRow.targetProduct + totalRow.targetItem;
    const totalActual = totalRow.actualProduct + totalRow.actualItem;
    const totalRate = totalTarget > 0 ? totalActual / totalTarget : 0;

    // 화면과 동일한 2단 헤더 + 데이터 + Total 행
    const aoa: any[][] = [
      ['담당자', '기간 목표 합계', '', '', '기간 실적 누적', '', '', '달성률'], // 🚨 [수정] 헤더 텍스트를 기간 기준으로 변경
      ['', '제품', '상품', '합계', '제품', '상품', '합계', ''],
      ...rows.map((r) => [
        r.name, r.targetProduct, r.targetItem, r.targetTotal,
        r.actualProduct, r.actualItem, r.actualTotal, r.rate
      ]),
      ['Total', totalRow.targetProduct, totalRow.targetItem, totalTarget, totalRow.actualProduct, totalRow.actualItem, totalActual, totalRate]
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // 헤더 병합(화면 구조와 동일)
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // 담당자
      { s: { r: 0, c: 1 }, e: { r: 0, c: 3 } }, // 목표 그룹
      { s: { r: 0, c: 4 }, e: { r: 0, c: 6 } }, // 실적 그룹
      { s: { r: 0, c: 7 }, e: { r: 1, c: 7 } }  // 달성률
    ];

    // 컬럼 폭
    ws['!cols'] = [
      { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
      { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 10 }
    ];

    // 숫자 포맷(#,##0) + 퍼센트(0.0%)
    const dataStartRow = 2;
    const dataEndRow = dataStartRow + rows.length; // Total 포함

    for (let r = dataStartRow; r <= dataEndRow; r++) {
      for (let c = 1; c <= 6; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (ws[addr]) ws[addr].z = '#,##0';
      }
      const rateAddr = XLSX.utils.encode_cell({ r, c: 7 });
      if (ws[rateAddr]) ws[rateAddr].z = '0.0%';
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '매출세부내역');

    // 🚨 [수정] 파일명에 선택된 기간을 명시
    XLSX.writeFile(wb, `sales_analysis_${startDate}_to_${endDate}.xlsx`);
  }


  // =================================================================
  // 🎨 [SECTION 5] 화면 렌더링 (UI Rendering)
  // =================================================================

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">
            <TrendingUp className="text-blue-600" size={24} />
            담당자별 목표 대비 실적 상세
          </h2>
          <p className="text-xs text-slate-500 mt-1 font-medium ml-8">
            설정된 기간 동안의 목표(Target)와 실제 달성한 실적(Actual)을 정밀 분석합니다.
          </p>
        </div>

        <div className="flex items-center gap-2 bg-white p-1.5 rounded-xl border border-slate-200 shadow-sm">          
          {/* 엑셀 다운로드 버튼 */}
          <button
            onClick={handleExportAnalysisExcel}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-[13px] font-black text-emerald-700 bg-blue-50/50 border hover:bg-emerald-100 transition-colors"
          >
            <FileSpreadsheet size={16} />
            엑셀 다운로드
          </button>

          {/* 구분선 */}
          <div className="w-px h-7 bg-slate-200" />

          {/* 🚨 [수정] 날짜 필터 (드롭다운 제거 및 달력 UI 추가) */}
          <div className="flex items-center gap-2 px-3 py-1 bg-slate-50 rounded-lg border border-slate-100">
            <Calendar size={14} className="text-slate-400" />
            <input 
              type="date" 
              value={startDate} 
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-transparent text-[12px] font-black text-slate-700 outline-none cursor-pointer"
            />
            <span className="text-slate-300 font-bold">~</span>
            <input 
              type="date" 
              value={endDate} 
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent text-[12px] font-black text-slate-700 outline-none cursor-pointer"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-sm text-slate-700">
            <colgroup>
              <col className="w-[140px]" />
              <col className="w-[120px]" />
              <col className="w-[120px]" />
              <col className="w-[120px]" />
              <col className="w-[120px]" />
              <col className="w-[120px]" />
              <col className="w-[120px]" />
              <col className="w-[110px]" />
            </colgroup>
            <thead>
              <tr className="bg-slate-100 text-slate-500 text-xs font-bold uppercase tracking-wider border-b border-slate-200">
                <th rowSpan={2} className="px-4 py-3 text-center border-r border-slate-200 w-[120px]">담당자</th>
                <th colSpan={3} className="px-4 py-2 text-center border-r border-slate-200 text-blue-700 bg-blue-50">🎯 기간 목표 합계</th>
                <th colSpan={3} className="px-4 py-2 text-center border-r border-slate-200 text-emerald-700 bg-emerald-50">🏆 기간 실적 누적</th>
                <th rowSpan={2} className="px-4 py-3 text-center w-[100px]">달성률</th>
              </tr>
              <tr className="bg-slate-50 text-[11px] text-slate-400 font-bold border-b border-slate-200">
                <th className="px-2 py-2 text-center bg-blue-50/30">제품</th>
                <th className="px-2 py-2 text-center bg-blue-50/30">상품</th>
                <th className="px-2 py-2 text-center border-r border-slate-200 bg-blue-50/50 text-slate-700">합계</th>
                <th className="px-2 py-2 text-center bg-emerald-50/30">제품</th>
                <th className="px-2 py-2 text-center bg-emerald-50/30">상품</th>
                <th className="px-2 py-2 text-center border-r border-slate-200 bg-emerald-50/50 text-slate-700">합계</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {performanceData.map((row, idx) => {
                const targetTotal = row.targetProduct + row.targetItem;
                const actualTotal = row.actualProduct + row.actualItem;
                const rate = targetTotal > 0 ? (actualTotal / targetTotal) * 100 : 0;
                return (
                  <tr key={idx} className="hover:bg-slate-50 transition-colors group">
                    <td className="px-4 py-3 font-bold text-center text-slate-800 border-r border-slate-100 bg-slate-50/30">{row.name}</td>
                    <td className="px-2 py-3 text-right text-slate-400 font-mono text-xs">{row.targetProduct.toLocaleString()}</td>
                    <td className="px-2 py-3 text-right text-slate-400 font-mono text-xs">{row.targetItem.toLocaleString()}</td>
                    <td className="px-2 py-3 text-right font-bold text-slate-600 border-r border-slate-100 bg-blue-50/10 font-mono text-xs">{targetTotal.toLocaleString()}</td>
                    <td className="px-2 py-3 text-right text-slate-600 font-mono text-xs">{row.actualProduct.toLocaleString()}</td>
                    <td className="px-2 py-3 text-right text-slate-600 font-mono text-xs">{row.actualItem.toLocaleString()}</td>
                    <td className="px-2 py-3 text-right font-black text-emerald-600 border-r border-slate-100 bg-emerald-50/10 font-mono text-xs">{actualTotal.toLocaleString()}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-black border ${
                        rate >= 100 ? 'bg-green-100 text-green-700 border-green-200' : 
                        rate >= 80 ? 'bg-blue-50 text-blue-600 border-blue-100' : 
                        'bg-slate-100 text-slate-400 border-slate-200'
                      }`}>{rate.toFixed(1)}%</span>
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-slate-100 border-t-2 border-slate-300 font-black text-xs">
                <td className="px-4 py-3 text-center text-slate-600 border-r border-slate-300">Total</td>
                <td className="px-2 py-3 text-right font-mono">{totalRow.targetProduct.toLocaleString()}</td>
                <td className="px-2 py-3 text-right font-mono">{totalRow.targetItem.toLocaleString()}</td>
                <td className="px-2 py-3 text-right border-r border-slate-300 font-mono">{(totalRow.targetProduct + totalRow.targetItem).toLocaleString()}</td>
                <td className="px-2 py-3 text-right font-mono">{totalRow.actualProduct.toLocaleString()}</td>
                <td className="px-2 py-3 text-right font-mono">{totalRow.actualItem.toLocaleString()}</td>
                <td className="px-2 py-3 text-right text-emerald-700 border-r border-slate-300 font-mono">{(totalRow.actualProduct + totalRow.actualItem).toLocaleString()}</td>
                <td className="px-4 py-3 text-center text-blue-700">
                  {((totalRow.actualProduct + totalRow.actualItem) / (totalRow.targetProduct + totalRow.targetItem || 1) * 100).toFixed(1)}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="p-3 border-t border-slate-100 flex justify-end bg-slate-50/50">
          <span className="text-[11px] font-bold text-slate-400 bg-white border px-2 py-1 rounded">단위: 원 (VAT 별도)</span>
        </div>
      </div>
    </div>
  );
};

export default SalesAnalysis;